import { useParams } from "react-router-dom";

import { useHospitalDetail } from "../../../hooks/queries/admin/hospitalDetails";
import {
  AdminBreadcrumb,
  ErrorMessage,
  PageContainer,
  PageHeader,
  PageIntro,
  PageTitle,
} from "../shared";
import { HospitalDetailsPanel } from "./HospitalDetails";

/**
 * Hospital details route (EN-5172 + EN-5175) — the page shell around
 * `HospitalDetailsPanel`.
 *
 * Retool navigated here with a `hospitalId` URL parameter and read
 * `urlparams.hospitalId`; a path parameter makes a hospital linkable the
 * ordinary way. A route rather than a drawer for the same reason as the other
 * detail pages: the panel embeds audit trails and drill-downs that need the
 * global modal slot.
 *
 * Gated by `hospitals:access`, the Hospitals list's own permission — the
 * established pattern for a detail route (Ward Details, Staff Details, Tenant
 * Details all reuse their list's code) and no menu entry of its own.
 */
export function AdminHospitalDetails() {
  const { hospitalId } = useParams<{ hospitalId: string }>();
  const parsedHospitalId = Number(hospitalId);
  const isValidHospitalId =
    Number.isFinite(parsedHospitalId) && parsedHospitalId > 0;

  const { hospital } = useHospitalDetail(
    isValidHospitalId ? parsedHospitalId : undefined
  );

  // Falls back to the id until the record loads, so the crumb never blinks
  // through an empty string.
  const currentLabel =
    hospital?.name ||
    (isValidHospitalId ? `Hospital ${parsedHospitalId}` : "Hospital details");

  document.title = `${currentLabel} - Admin`;

  return (
    <PageContainer>
      <PageIntro>
        <AdminBreadcrumb
          items={[
            { label: "Hospitals", to: "/admin/hospitals" },
            { label: currentLabel },
          ]}
        />
        <PageHeader>
          <PageTitle>{currentLabel}</PageTitle>
        </PageHeader>
      </PageIntro>

      {isValidHospitalId ? (
        <HospitalDetailsPanel hospitalId={parsedHospitalId} />
      ) : (
        <ErrorMessage>That hospital id isn&apos;t valid.</ErrorMessage>
      )}
    </PageContainer>
  );
}
